import {
  createElement,
  useCallback,
  useState,
  forwardRef,
  useEffect,
  useRef,
  Fragment,
  useMemo,
} from 'react';
import { getTabSpacingTokens, Tab } from '../__internal__/index';
import { Scrollable } from '../../Scrollable/index';
import { postfixTestId } from '../../../util/testId/index';
import { isNullish } from '@sas/nova-core/typeguards';
import useOnFocusOut from '../../../hooks/useOnFocusOut/index';
import { ReactKeyboardEvent } from '../../../react-synthetic-events';
import { createUseHtmlId } from '../../../hooks/useHtmlId/index';
import { useIsRTL } from '../../../locale/index';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import bundle from '../NovaComponents-Tab-gui-icu.properties';
import { Button, ButtonVariant } from '../../Button/index';
import { MenuButton, MenuButtonProps } from '../../MenuButton';
import { InvisibleText } from '../../InvisibleText';
import { MoveLeft, MoveRight, AddTab, TabOverflow } from '@sas/react-svg';
import { MenuSection, MenuItem } from '../../Menu';
import { usePreviousValue } from '../../../hooks/usePreviousValue/index';
import { setRef } from '../../../util/ref-util';
import {
  useDesignTokens,
  useInvertedThemeData,
  InverseTokenProvider,
  renderInlineCssVars,
} from '../../../theme/index';
import { setAlpha } from '../../../design-tokens';
import {
  BaseTabBarProps,
  DraggableTabProps,
  OverflowMenuButtonDisplayOptions,
  TabBarContainerTokens,
  TabBarVariant,
  TabEditActionType,
  TabItem,
  TabDragType,
} from '.';
import { DraggableTab } from './dnd/DraggableTab';
import { TabDragPreview } from './dnd/TabDragPreview';
import { DropTargetMonitor } from 'react-dnd';
import { useReactDndConstructs as useDragAndDropHooks } from '../../../dnd/ReactDndContext';
import { getDndStyleTokens } from '../../../style/reactDnd/DndStyleTokens';
import { useEarcon, EarconName } from '../../../hooks/useEarcon';

import cx from 'classnames';
import cn from './Tab.css.json';
import './Tab.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const tabBarThemeTokens: TabBarContainerTokens = (dt, props) => {
  const primaryLight = props.variant === TabBarVariant.PrimaryLight;

  if (dt.enableNovaTokens) {
    switch (dt.themeVariant) {
      case 'dark':
        return {
          bd: !props.alternate ? dt.c.colorUiBorder : undefined,
          'bd-bottom-width': !props.alternate ? '1px' : 0,
        };
      case 'high-contrast':
        return {
          bd: dt.c.colorUiBorder,
          'bd-bottom-width': '1px',
        };
      default:
        if (primaryLight) {
          return {
            bd: !props.alternate ? setAlpha(dt.c.colorTextInverse, 0.25) : undefined,
            'bd-bottom-width': !props.alternate ? '1px' : 0,
          };
        }
        return {
          bd: !props.alternate ? dt.c.colorUiBorder : undefined,
          'bd-bottom-width': !props.alternate ? '1px' : 0,
        };
    }
  }

  // Legacy Token Support
  switch (dt.themeVariant) {
    case 'dark':
      return {
        bd: !props.alternate ? dt.c.white15 : undefined,
        'bd-bottom-width': !props.alternate ? '1px' : 0,
      };
    case 'high-contrast':
      return {
        bd: dt.c.white,
        'bd-bottom-width': '1px',
      };
    default:
      return {
        bd: !props.alternate ? dt.c.n2 : undefined,
        'bd-bottom-width': !props.alternate ? '1px' : 0,
      };
  }
};

const useTabId = createUseHtmlId('TabBar-tab');
const useTabbarDndPrefixId = createUseHtmlId('TabBar-dndPrefix');
const useTabAriaDescribedById = createUseHtmlId('Tab-aria-description');

export const BaseTabBar = forwardRef<HTMLDivElement, BaseTabBarProps>(function BaseTabBar(
  props,
  ref
) {
  const { useDrop } = useDragAndDropHooks();
  const localizer = useTextLocalizer();
  const alternate = props.visualStyle === 'alternative';
  const variant = props.variant ?? TabBarVariant.Standard;
  const themeTransition = props.themeTransition === true;
  const invertedThemeContext = useInvertedThemeData();
  const { tokenOverrides, onItemSelected, focusedIndex = 0, setFocusedIndex } = props;
  const orientation = props.orientation ?? 'horizontal';
  const placement = props.placement ?? 'start';
  const stretchToFit = props.stretchToFit ?? false;
  const isVerticalOriented = orientation === 'vertical';
  const renderTabToolbar = props.renderTabToolbar;
  const role =
    !props.addNewTabButtonProps && !props.expandCollapseProps && !props.onTabMenuItemSelect
      ? undefined
      : props.role ?? 'group';

  const isRTL = useIsRTL();
  const ariaDescriptionId = useTabAriaDescribedById();

  const [areScrollButtonsVisible, setScrollButtonVisibility] = useState(false);
  const [isOverNonScrollableZone, setIsOverNonScrollableZone] = useState(false);
  const [overflowMenuSize, setOverflowMenuSize] = useState(0);
  const [addButtonSize, setAddButtonSize] = useState(0);
  const [scrollListSize, setScrollListSize] = useState(0);
  const [tabBarSize, setTabBarSize] = useState(0);

  /* ------------------------------------------------------------
   * Set Aria Label and tooltip for expand/collapse buttons
   * ------------------------------------------------------------ */
  const expandTooltip =
    props.expandCollapseProps?.expandTooltip ?? localizer('tabBar.default.expanded.label');
  const collapseTooltip =
    props.expandCollapseProps?.collapseTooltip ?? localizer('tabBar.default.collapsed.label');
  const expandAriaLabel = props.expandCollapseProps?.expandAriaLabel ?? expandTooltip;
  const collapseAriaLabel = props.expandCollapseProps?.collapseAriaLabel ?? collapseTooltip;
  const overflowMenuButtonPosition = props.overflowMenuButtonPosition ?? 'endOfTabList';
  const defaultDnDPrefix = useTabbarDndPrefixId();
  const dndPrefix = props.dndPrefix ?? defaultDnDPrefix;
  const dragType = props.dragType ?? TabDragType;
  const droppableTabs = props.dropTypes?.tabs ?? [TabDragType];
  const droppableContent = props.dropTypes?.content ?? [];
  const acceptDropTypes = Array.from(new Set([dragType, ...droppableTabs, ...droppableContent]));
  const tabBarRef = useRef<HTMLDivElement | null>(null);
  const tabBarScrollableRef = useRef<HTMLDivElement | null>(null);
  const addTabBtnRef = useRef<HTMLDivElement | null>(null);
  const overflowMenuRef = useRef<HTMLDivElement | null>(null);

  // FIXME: TabBar no longer requires overrides. Keeping this in place for now to prevent build issues.
  const dt = useDesignTokens();

  const tokens = {
    ...tabBarThemeTokens(dt, { alternate, variant }),
    ...(tokenOverrides && tokenOverrides(dt, { alternate, variant })),
  };

  const cssVars = renderInlineCssVars(
    {
      ...getTabSpacingTokens(dt),
      ...tokens,
    },
    'tab-bar'
  );

  const dndTokens = getDndStyleTokens(dt);
  // End fixme

  const selectIfNeeded = useCallback(
    (index: number, item: TabItem) => {
      setTabItemHasFocus(true);
      onItemSelected({ index, item });
      setFocusedIndex(index);
    },
    [onItemSelected, setFocusedIndex]
  );

  const [tabItemHasFocus, setTabItemHasFocus] = useState<boolean>(false);

  const [horizontalScrollIndex, setHorizontalScrollIndex] = useState(
    props.selectedIndex < 0 ? focusedIndex : props.selectedIndex
  );

  // Check if any item is movable
  const isItemsMovable = useMemo(() => {
    return props.items.some((item: TabItem) => item.allowDrag);
  }, [props.items]);

  const shouldDisplayContextMenu =
    props.editable ||
    props.items.some(
      (item: TabItem) => item.closable || (item.menuSections && item.menuSections.length > 0)
    );

  const areAllTabsCloseable = props.items.every((item) => item.closable);

  const prevItems = usePreviousValue<TabItem[]>(props.items);

  const getStrechToFitTabBarItemWidth = renderInlineCssVars(
    {
      'stretched-width':
        !areScrollButtonsVisible && tabBarScrollableRef.current && props.stretchToFit
          ? `${Math.floor(tabBarScrollableRef.current.clientWidth / props.items.length)}px`
          : undefined,
    },
    'tab-item'
  );
  const earcon = useEarcon({ source: EarconName.InputFull });

  useEffect(() => {
    setFocusedIndex(props.selectedIndex < 0 ? 0 : props.selectedIndex);
  }, [props.selectedIndex, setFocusedIndex]);

  useEffect(() => {
    let timeoutId: any; // Should really be number but VSCode type checking doesn't like it.
    if (!isNullish(prevItems) && prevItems.length !== props.items.length) {
      setHorizontalScrollIndex(props.selectedIndex);
      timeoutId = setTimeout(() => {
        setFocusedIndex(props.selectedIndex < 0 ? 0 : props.selectedIndex);
        setTabItemHasFocus(true);
      }, 0);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [prevItems, props.items, props.selectedIndex, setFocusedIndex]);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    const length = props.items.length;
    if (length <= 0) {
      return;
    }
    let preventDefault = true;
    const ctrl = e.ctrlKey;
    const shift = e.shiftKey;
    switch (e.key) {
      case 'ArrowDown': /* falls-through */
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        if (isNullish(focusedIndex)) {
          setFocusedIndex(focusedIndex);
        } else {
          if (focusedIndex < length - 1) {
            if (
              ctrl &&
              shift &&
              props.items[horizontalScrollIndex].allowDrag &&
              !props.items[horizontalScrollIndex].disabled
            ) {
              props.reorderTabs?.(horizontalScrollIndex, focusedIndex + 1);
            }
            setFocusedIndex(focusedIndex + 1);
          } else {
            if (
              ctrl &&
              shift &&
              props.items[horizontalScrollIndex].allowDrag &&
              !props.items[horizontalScrollIndex].disabled
            ) {
              props.reorderTabs?.(horizontalScrollIndex, focusedIndex);
            }
            setFocusedIndex(focusedIndex);
            if (focusedIndex + 1 === length) earcon.play();
          }
        }
        break;
      case 'ArrowUp': /* falls-through */
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        if (isNullish(focusedIndex)) {
          setFocusedIndex(focusedIndex);
        } else {
          if (focusedIndex > 0) {
            if (
              ctrl &&
              shift &&
              props.items[horizontalScrollIndex].allowDrag &&
              !props.items[horizontalScrollIndex].disabled
            ) {
              props.reorderTabs?.(horizontalScrollIndex, focusedIndex - 1);
            }
            setFocusedIndex(focusedIndex - 1);
          } else {
            if (
              ctrl &&
              shift &&
              props.items[horizontalScrollIndex].allowDrag &&
              !props.items[horizontalScrollIndex].disabled
            ) {
              props.reorderTabs?.(horizontalScrollIndex, focusedIndex);
            }
            setFocusedIndex(focusedIndex);
            if (focusedIndex === 0) earcon.play();
          }
        }
        break;
      case 'Home':
        if (
          ctrl &&
          shift &&
          props.items[horizontalScrollIndex].allowDrag &&
          !props.items[horizontalScrollIndex].disabled
        ) {
          props.reorderTabs?.(horizontalScrollIndex, 0);
        }
        setFocusedIndex(0);
        if (focusedIndex === 0) earcon.play();
        break;
      case 'End':
        if (
          ctrl &&
          shift &&
          props.items[horizontalScrollIndex].allowDrag &&
          !props.items[horizontalScrollIndex].disabled
        ) {
          props.reorderTabs?.(horizontalScrollIndex, length - 1);
        }
        setFocusedIndex(length - 1);
        if (focusedIndex + 1 === length) earcon.play();
        break;
      case ' ': /* falls-through */
      case 'Enter':
        if (!props.items[focusedIndex].disabled)
          onItemSelected?.({ index: focusedIndex, item: props.items[focusedIndex] });
        break;
      case 'PageUp':
        setFocusedIndex(0);
        if (focusedIndex === 0) earcon.play();
        break;
      case 'PageDown':
        setFocusedIndex(length - 1);
        if (focusedIndex + 1 === length) earcon.play();

        break;

      default:
        preventDefault = false;
        break;
    }
    if (preventDefault) {
      e.preventDefault();
    }
  };

  const { onBlur, onFocus } = useOnFocusOut(() => {
    setTabItemHasFocus(false);
    props.selectedIndex > -1 && setFocusedIndex(props.selectedIndex);
  });
  const isTabFocusable = (index: number) => {
    return index === focusedIndex;
  };

  const defaultTabIds: string[] = useTabId(props.items.length);
  const tabIds: string[] = props.items.map(({ id }, index) => id ?? defaultTabIds[index]);

  const onOverflowItemSelected = (index: number) => {
    setHorizontalScrollIndex(index);
    props.onItemSelected({
      index,
      item: props.items[index],
    });
  };

  const onOverflowItemClosed = (index: number) => {
    props.onTabClose?.(index);
  };

  const [{ isOverFlowMenu }, overFlowBtnDropConnect] = useDrop({
    accept: acceptDropTypes,
    canDrop: () => false,
    collect(monitor) {
      let isOverFlowMenu = false;
      if (
        monitor.isOver() &&
        monitor.getItem().dndPrefix !== dndPrefix &&
        !droppableContent.includes(monitor.getItem()?.type)
      ) {
        isOverFlowMenu = true;
      }
      return {
        isOverFlowMenu,
      };
    },
  });

  const updateIsOverNonScrollableZoneState = (monitor: DropTargetMonitor<any, any>) => {
    if (props.allowDrop) {
      let overNonScrollableZone = false;
      if (
        monitor.canDrop() &&
        monitor.isOver() &&
        !areScrollButtonsVisible &&
        !droppableContent.includes(monitor.getItem()?.type)
      ) {
        const contentSize = scrollListSize + addButtonSize;
        if (!isVerticalOriented && monitor.getItem().dndPrefix !== dndPrefix) {
          if (isRTL && monitor.getClientOffset()!.x < contentSize) {
            overNonScrollableZone = true;
          } else if (!isRTL && monitor.getClientOffset()!.x > contentSize) {
            overNonScrollableZone = true;
          }
        } else if (
          isVerticalOriented &&
          monitor.getItem().dndPrefix !== dndPrefix &&
          monitor.getClientOffset()!.y > contentSize
        ) {
          overNonScrollableZone = true;
        }
      }

      setIsOverNonScrollableZone(overNonScrollableZone);
    }
  };

  const [{}, nonScrollZoneDropConnect] = useDrop({
    accept: acceptDropTypes,
    canDrop: (item: DraggableTabProps) => {
      return item.dndPrefix !== dndPrefix ? props.allowDrop ?? true : false;
    },
    drop: (item: DraggableTabProps, monitor) => {
      if (
        !isOverFlowMenu &&
        !areScrollButtonsVisible &&
        !monitor.didDrop() &&
        dndPrefix !== item.dndPrefix &&
        !droppableContent.includes(monitor.getItem()?.type) &&
        !monitor.getDropResult()?.isBaseScrollableButtons
      ) {
        props.onDrop?.({
          droppedItem: item,
          dropIndex: props.items.length,
          monitor,
        });
      }
    },
    collect(monitor) {
      updateIsOverNonScrollableZoneState(monitor);
      return {};
    },
    hover: (_: any, monitor) => {
      updateIsOverNonScrollableZoneState(monitor);
    },
  });
  nonScrollZoneDropConnect(tabBarRef);

  const [{ isOverTabBarScrollable }, tabBarScrollDropZoneConnect] = useDrop({
    accept: acceptDropTypes,
    canDrop: (item: DraggableTabProps) => {
      return item.dndPrefix !== dndPrefix ? props.allowDrop ?? true : false;
    },
    collect(monitor) {
      let isOver = false;
      if (
        props.allowDrop &&
        monitor.isOver() &&
        !areScrollButtonsVisible &&
        !droppableContent.includes(monitor.getItem()?.type)
      ) {
        isOver = true;
      }
      return { isOverTabBarScrollable: isOver };
    },
  });
  tabBarScrollDropZoneConnect(tabBarScrollableRef);

  const [{ isOverAddTabBtn }, addTabBtnDropConnect] = useDrop({
    accept: acceptDropTypes,
    canDrop: (item: DraggableTabProps) =>
      item.dndPrefix !== dndPrefix ? props.allowDrop ?? true : false,
    drop: (item: DraggableTabProps, monitor) => {
      if (
        !monitor.didDrop() &&
        dndPrefix !== item.dndPrefix &&
        !droppableContent.includes(monitor.getItem()?.type)
      ) {
        props.onDrop?.({
          droppedItem: item,
          dropIndex: props.items.length,
          monitor,
        });
      }
    },
    collect(monitor) {
      let isOverAddTabBtn = false;
      if (
        monitor.isOver() &&
        monitor.getItem().dndPrefix !== dndPrefix &&
        !droppableContent.includes(monitor.getItem()?.type)
      ) {
        isOverAddTabBtn = true;
      }
      return {
        isOverAddTabBtn,
      };
    },
  });

  addTabBtnDropConnect(addTabBtnRef);

  const getOverflowMenuSize = (position: 'endOfTabList' | 'endOfTabBar') => {
    const displayOverflowMenu = shouldOverflowMenuBeDisplayed(
      props.overflowMenuButtonDisplay ?? 'never',
      areScrollButtonsVisible
    );

    return displayOverflowMenu && position === overflowMenuButtonPosition ? overflowMenuSize : 0;
  };

  const dropZoneAreaStartPosition = renderInlineCssVars(
    {
      'start-position': isRTL
        ? `${getOverflowMenuSize('endOfTabBar')}px`
        : `calc(${scrollListSize}px + ${addButtonSize}px + ${getOverflowMenuSize(
            'endOfTabList'
          )}px - ${tabBarSize}px)`,
      'end-position': isRTL
        ? `calc(${tabBarSize}px - ${scrollListSize}px + ${addButtonSize}px + ${getOverflowMenuSize(
            'endOfTabList'
          )}px)`
        : `${getOverflowMenuSize('endOfTabBar')}px`,
    },
    'drop-zone'
  );

  const dropZoneAreaStartVerticalPosition = renderInlineCssVars(
    {
      'start-position': `calc(${scrollListSize}px + ${addButtonSize}px + ${getOverflowMenuSize(
        'endOfTabList'
      )}px )`,
      'end-position': `${getOverflowMenuSize('endOfTabBar')}px`,
    },
    'drop-zone'
  );

  function renderOverflowMenuButton() {
    const overflowMenuButtonTooltip =
      props.overflowMenuButtonTooltip ?? localizer('tabBar.overflowMenuButton.label');

    const overflowMenuButtonAriaLabel =
      props.overflowMenuButtonAriaLabel ?? overflowMenuButtonTooltip;

    const updateOverflowButtonLabel = isVerticalOriented && props.expandCollapseProps?.isExpanded;

    let shouldSetOverflowButtonLabel = false;

    const overflowMenuSection: MenuSection = {
      items: props.items.map(
        (item, index): MenuItem => {
          if (updateOverflowButtonLabel && !shouldSetOverflowButtonLabel && item.label) {
            shouldSetOverflowButtonLabel = true;
          }
          return {
            text: item.label ?? item.icon?.tooltip ?? '',
            checked: index === props.selectedIndex,
            closable: !!item.closable,
          };
        }
      ),
    };

    const buttonProps = {
      testId: postfixTestId(props.testId, 'overflowMenu'),
      icon: { icon: TabOverflow },
      text: shouldSetOverflowButtonLabel ? localizer('tabBar.overflowMenuButton.label') : undefined,
      tooltip: overflowMenuButtonTooltip,
      ariaLabel: overflowMenuButtonAriaLabel,
    };

    const overflowMenuButtonProps: MenuButtonProps = {
      ...buttonProps,
      variant: ButtonVariant.Icon,
      hideDownArrow: true,
      sections: [overflowMenuSection],
      onItemSelected: (location) => {
        onOverflowItemSelected(location[0][1]);
      },
      onItemClosed: (location) => onOverflowItemClosed(location[0][1]),
    };

    overFlowBtnDropConnect(overflowMenuRef);

    return (
      <div
        ref={(e) => {
          if (e) {
            overflowMenuRef.current = e;
            const size = isVerticalOriented
              ? e.getBoundingClientRect().height
              : e.getBoundingClientRect().width;
            setOverflowMenuSize(size);
          }
        }}
        className={cx(
          cn.tabOverFlow,
          props.overflowMenuButtonDisplay &&
            overflowMenuButtonPosition === 'endOfTabBar' &&
            cn.endOfTabBar
        )}
      >
        {props.renderOverflowMenuButton ? (
          props.renderOverflowMenuButton(
            // button props
            buttonProps,
            // menu props
            {
              onItemSelected: onOverflowItemSelected,
              onItemClosed: onOverflowItemClosed,
              items: props.items,
              selectedIndex: props.selectedIndex,
            }
          )
        ) : (
          <MenuButton
            menuAlign={{
              horizontalAlign: 'end',
            }}
            className={cn.tabOverflowButton}
            {...overflowMenuButtonProps}
          />
        )}
      </div>
    );
  }

  function shouldOverflowMenuBeDisplayed(
    overflowMenuDisplayType: OverflowMenuButtonDisplayOptions,
    isScrollVisible: boolean
  ) {
    switch (overflowMenuDisplayType) {
      case 'always':
        return true;
      case 'onVisibleScrollbar':
        return isScrollVisible;
      default:
        return false;
    }
  }

  // renders all controls outside of the tabs section
  function renderNonTabsContent() {
    const displayOverflowMenu = shouldOverflowMenuBeDisplayed(
      props.overflowMenuButtonDisplay ?? 'never',
      areScrollButtonsVisible
    );

    return (
      <Fragment>
        {!isNullish(props.renderAddNewTabButton) && (
          <div className={cn.addButtonContainer}>
            {props.renderAddNewTabButton({ icon: { icon: AddTab } })}
          </div>
        )}
        {isNullish(props.renderAddNewTabButton) && !isNullish(props.addNewTabButtonProps) && (
          <div
            ref={(e) => {
              if (e) {
                addTabBtnRef.current = e;
                const size = isVerticalOriented
                  ? e.getBoundingClientRect().height
                  : e.getBoundingClientRect().width;
                setAddButtonSize(size);
              }
            }}
            className={cn.addButtonContainer}
          >
            <Button
              testId={postfixTestId(props.testId, 'addNewTabBtn')}
              icon={{ icon: AddTab }}
              variant={ButtonVariant.SmallIcon}
              className={cx(cn.addButton, isOverAddTabBtn && cn.dropZone)}
              onPress={() => {
                props.addNewTabButtonProps?.onPress();
              }}
              {...(props.addNewTabButtonProps.ariaLabel && {
                ariaLabel: props.addNewTabButtonProps.ariaLabel,
              })}
              {...(props.addNewTabButtonProps.title && {
                tooltip: props.addNewTabButtonProps.title,
              })}
            />
          </div>
        )}

        {displayOverflowMenu && renderOverflowMenuButton()}
        {renderTabToolbar && (
          <div style={{ width: props.toolbarWidth }} className={cn.toolbar}>
            {renderTabToolbar()}
          </div>
        )}
        {isVerticalOriented && !isNullish(props.expandCollapseProps) && (
          <Button
            testId={postfixTestId(props.testId, 'expandCollapseBtn')}
            className={cx(
              cn.expandCollapseButton,
              placement === 'end' ? cn.alignStart : cn.alignEnd,
              props.expandCollapseProps.isExpanded && cn.expanded
            )}
            tooltip={props.expandCollapseProps.isExpanded ? expandTooltip : collapseTooltip}
            ariaLabel={props.expandCollapseProps.isExpanded ? expandAriaLabel : collapseAriaLabel}
            icon={{
              icon:
                (placement === 'start' && !props.expandCollapseProps.isExpanded) ||
                (placement === 'end' && props.expandCollapseProps.isExpanded)
                  ? MoveRight
                  : MoveLeft,
            }}
            onPress={props.expandCollapseProps.onExpand}
            variant={ButtonVariant.Light}
          />
        )}
      </Fragment>
    );
  }

  function renderTabsContainer() {
    const isTabAllowDrag = props.items.every((item: TabItem) => item.allowDrag);
    const isDisplayContextMenu = shouldDisplayContextMenu;

    let ariaDescriptionText = '';
    if (isDisplayContextMenu && isTabAllowDrag) {
      ariaDescriptionText = `${localizer('tab.editLabelMessage.aria')} ${localizer(
        'tabs.rearranged.message.aria'
      )}`;
    } else if (isDisplayContextMenu) {
      ariaDescriptionText = localizer('tab.editLabelMessage.aria');
    } else if (isTabAllowDrag) {
      ariaDescriptionText = localizer('tabs.rearranged.message.aria');
    }

    // JSX for Tabs
    const tabsTemplate = props.items.map((item, index) => {
      return (
        <InverseTokenProvider
          key={index}
          inverseTheme={
            (index === props.selectedIndex && themeTransition && alternate) || invertedThemeContext
          }
        >
          {item.allowDrag || props.allowDrop
            ? renderDraggableTab(index, item)
            : renderTab(index, item)}
        </InverseTokenProvider>
      );
    });

    // JSX for TabBarContent
    const tabBarContent = (
      <div
        ref={(e) => {
          if (e) {
            tabBarRef.current = e;

            const size = isVerticalOriented
              ? e.querySelector('ul')?.getBoundingClientRect().height ?? 0
              : isRTL
              ? e.querySelector('ul')?.getBoundingClientRect().left ?? 0
              : e.querySelector('ul')?.getBoundingClientRect().right ?? 0;

            setScrollListSize(size);

            setTabBarSize(
              isRTL
                ? tabBarRef?.current?.getBoundingClientRect().right ?? 0
                : tabBarRef?.current?.getBoundingClientRect().left ?? 0
            );
          }
        }}
        style={{
          ...cssVars,
          ...(props.allowDrop && dndTokens),
          ...(!isVerticalOriented && dropZoneAreaStartPosition),
          ...(isVerticalOriented && dropZoneAreaStartVerticalPosition),
          ...getStrechToFitTabBarItemWidth,
          ...props.style,
        }}
        className={cx(
          isVerticalOriented ? cn.vertical : cn.horizontal,
          isVerticalOriented &&
            !isNullish(props.expandCollapseProps) &&
            !props.expandCollapseProps.isExpanded &&
            cn.collapsed,
          isVerticalOriented && placement === 'end' && cn.placeEnd,
          isVerticalOriented && !props.showSelectionIndicator && cn.hideIndicator,
          stretchToFit && !areScrollButtonsVisible && cn.stretchToFit,
          isOverNonScrollableZone &&
            !isOverAddTabBtn &&
            !isOverFlowMenu &&
            !isOverTabBarScrollable &&
            cn.droppable,
          (props.addNewTabButtonProps ||
            (props.overflowMenuButtonDisplay && overflowMenuButtonPosition === 'endOfTabList')) &&
            cn.tabListHeightAuto,
          props.className
        )}
        role={role}
        aria-label={props.ariaLabel}
        data-landmark-label={props.landmarkLabel}
      >
        {isVerticalOriented && props.headerElement?.()}
        <Scrollable
          ref={(e) => {
            setRef(ref, e);
            tabBarScrollableRef.current = e;
          }}
          acceptDropTypes={acceptDropTypes}
          orientation={orientation}
          currentIndex={horizontalScrollIndex}
          setCurrentIndex={setHorizontalScrollIndex}
          className={cx(cn.bar, props.className)}
          contentClassName={cn.contentList}
          testId={props.testId}
          selectedIndex={props.selectedIndex}
          contentProps={{
            onKeyDown,
            onBlur,
            onFocus: () => {
              if (!tabItemHasFocus) {
                setTabItemHasFocus(true);
              }
              onFocus();
            },
            role: 'tablist',
            // 'aria-owns' is needed for JAWS to work properly because in our DOM,
            // "tab" elements are not direct children of "tablist" element.
            // See details in https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Tab_Role
            'aria-owns': tabIds.join(' '),
            'aria-orientation': orientation,
          }}
          contentAriaDescribedBy={ariaDescriptionText !== '' ? ariaDescriptionId : undefined}
          onOverflowChange={setScrollButtonVisibility}
        >
          {tabsTemplate}
        </Scrollable>
        <InvisibleText id={ariaDescriptionId}>{ariaDescriptionText}</InvisibleText>
        {renderNonTabsContent()}
        <TabDragPreview dragType={dragType} dndPrefix={dndPrefix}>
          {(index: number) => {
            return renderTab(index, props.items[index]);
          }}
        </TabDragPreview>
      </div>
    );

    return tabBarContent;
  }

  function renderTab(index: number, item: TabItem) {
    if (item === undefined) {
      return <Fragment />;
    }
    return (
      <Tab
        altId={props.altId}
        id={tabIds[index]}
        key={index}
        {...item}
        selected={index === props.selectedIndex}
        visualStyle={props.visualStyle}
        variant={props.variant}
        onPress={() => selectIfNeeded(index, item)}
        onClose={() => {
          props.onTabClose?.(index);
        }}
        onCloseAll={props.onTabCloseAll}
        testId={item.testId ?? postfixTestId(props.testId, `tab${index}`)}
        stretchToFitTab={stretchToFit && !areScrollButtonsVisible}
        closeTabButtonTooltip={props.closeTabButtonTooltip}
        closeTabButtonkeyboardTooltip={props.closeTabButtonkeyboardTooltip}
        hideCloseAllTabOption={!props.hideCloseAllTabOption && areAllTabsCloseable}
        showContextMenu={shouldDisplayContextMenu}
        menuButtonTooltip={props.menuButtonTooltip}
        location={
          alternate && !isVerticalOriented
            ? 'top'
            : isVerticalOriented
            ? placement === 'end'
              ? 'right'
              : 'left'
            : 'bottom'
        }
        orientation={props.orientation}
        tokenOverrides={props.tabOverrides}
        {...(!isNullish(item.menuSections) && {
          menuProps: {
            menuSections: item.menuSections,
            onMenuItemSelect: (location, menuSections) =>
              props.onTabMenuItemSelect?.(index, { ...item, menuSections }, location),
            onOpen: () => {
              props.onTabMenuOpen?.(index, item);
            },
          },
        })}
        closeTabMenuItemText={props.closeTabMenuItemText}
        closeAllTabMenuItemText={props.closeAllTabMenuItemText}
        renameMenuItemPosition={props.renameMenuItemPosition}
        showRenameMenuOption={props.showRenameMenuOption}
        showCloseMenuOption={props.showCloseMenuOption}
        focused={tabItemHasFocus && index === focusedIndex}
        tabIndex={isTabFocusable(index) ? 0 : -1}
        editable={props.editable}
        onLabelChange={(newValue: string, type: TabEditActionType) =>
          props.onTabLabelChange?.(index, item, newValue, type)
        }
        onFocus={() => {
          setHorizontalScrollIndex(index);
          if (index !== focusedIndex) {
            setFocusedIndex(index);
          }
        }}
        allowDrag={item.allowDrag}
        movableTab={isItemsMovable}
      />
    );
  }

  function renderDraggableTab(index: number, item: TabItem) {
    return (
      <DraggableTab
        id={tabIds[index]}
        type={dragType}
        index={index}
        dndPrefix={dndPrefix}
        reorderTabs={props.reorderTabs}
        allowDrop={!!props.allowDrop}
        allowDrag={item.disabled === true ? false : !!item.allowDrag}
        acceptDropTypes={acceptDropTypes}
        droppableContent={droppableContent}
        onDragEnd={props.onDragEnd}
        onDrop={props.onDrop}
        onFocus={() => {
          setHorizontalScrollIndex(focusedIndex);
          if (index !== focusedIndex) {
            setFocusedIndex(focusedIndex);
          }
        }}
        orientation={props.orientation}
      >
        {item && renderTab(index, item)}
      </DraggableTab>
    );
  }

  return renderTabsContainer();
});
